import React, {useEffect, useContext, useRef, useState} from 'react'
import { useIntl } from 'react-intl'
import { format, lastDayOfMonth, parse, startOfMonth, subMonths } from 'date-fns'
import { Chart } from 'primereact/chart'
import { AuthContext } from 'Context'
import useGetData from 'Hooks/useGetData'
import { Section, Spacer } from 'Components/layout'
import { Button, Column, DataTable, ToolPanel } from 'Components/readonly'
import { Monthpicker, Dropdown } from "Components/input";
import { DateRangePicker } from 'Components/input/dateRangePicker'
import colors from 'Components/colors.scss'
import axios from "axios";

export const ReportProducts = () => {
  const intl = useIntl()
  const dataTableRef = useRef()
  const hasMounted = useRef(false)
  const { activeMarket } = useContext(AuthContext)
  const [row, setRow] = useState(null)
  const [month, setMonth] = useState(startOfMonth(subMonths(new Date(), 1)))
  const [fromDate, setFromDate] = useState(startOfMonth(subMonths(new Date(), 13)))
  const [toDate, setToDate] = useState(lastDayOfMonth(subMonths(new Date(), 1)))
  const [monthType, setSelectedMonthType] = useState('calendar')
  const [isLoading, customersProducts] = useGetData(
    '/rapi/admin/report-customers-products?month=' + format(parse(month), 'YYYY-MM-DD') + "&type=" + monthType,
    [],
    [month]
  )
  const [chartData, setChartData] = useState(null)
  const chartOptions = {
    legend: {
      display: true,
      labels: {
        fontColor: colors.white
      }
    },
    scales: {
      xAxes: [
        {
          type: 'time',
          time: {
            displayFormats: {
              millisecond: 'MMM DD HH:mm:ss',
              second: 'MMM DD HH:mm:ss',
              minute: 'MMM DD HH:mm:ss',
              hour: 'MMM DD HH:mm',
              day: 'MMM DD',
              week: 'YYYY-MM-DD',
              month: 'YYYY-MM-DD',
              quarter: 'YYYY-MM-DD',
              year: 'YYYY-MM-DD'
            }
          },
          ticks: {
            fontColor: colors.white
          }
        }
      ],
      yAxes: [
        {
          ticks: {
            fontColor: colors.white,
            precision: 0
          }
        }
      ]
    }
  }

  const paintChart = (label, data) => {
    setChartData({
      datasets: [
        {
          label: label,
          data: data,
          fill: false,
          backgroundColor: colors.primary,
          borderColor: colors.primary,
          lineTension: 0
        }
      ]
    });
  }

  const monthTypeOptions = [
    { label: 'Kalendermånad', value: 'calendar' },
    { label: '25e - 25e', value: 'custom' }
  ]

  const handleMonthTypeChange = (e) => {
    setSelectedMonthType(e.target.value)
  }

  /**
   * Will fetch report and statistics data in accordion and for line chart.
   * This effect is triggered on row, toDate or fromDate change.
   */
  useEffect(() => {
    // Prevents any class on an initial page load
    if (hasMounted.current) {
      const date = 'start=' + format(parse(fromDate), 'YYYY-MM-DD') + '&end=' + format(parse(toDate), 'YYYY-MM-DD');
      const fetchData = async () => {
        try {
          const result = await axios.get('/rapi/admin/report-customers-products/per-day?org-id=' + row.id + "&" + date);
          const lineData = result.data.map((obj) => {
            return {
              x: parse(obj.added_date),
              y: obj.chemical_count
            }
          });
          paintChart(row.name, lineData);

        } catch (e) {
          console.error(e)
        }
      };
      fetchData();

    } else {
      hasMounted.current = true;
    }
  }, [row, fromDate, toDate])

  /**
   * Set row data when click on an organization on table, this will in turn trigger the useEffect.
   *
   * @param e
   * @returns {Promise<void>}
   */
  const handleRowClick = async (e) => {
    setRow(e.data);
  }

  const exportCSV = () => {
    if (dataTableRef.current) {
      dataTableRef.current.exportCSV()
    }
  }

  return (
    <>
      <ToolPanel>
        <Dropdown
          options={monthTypeOptions}
          value={monthType}
          onChange={handleMonthTypeChange}
          style={{ width: '170px', height: '34px', lineHeight: '14px' }}
        />
        <Monthpicker
          value={month}
          onChange={(e) => setMonth(e.value)}
          readOnlyInput={true}
        />
        <Spacer/>
        <Button icon="pi pi-file-excel" label={intl.formatMessage({ id: 'admin.export_csv' })} onClick={exportCSV}/>
      </ToolPanel>
      <Section>
        <DataTable
          ref={dataTableRef}
          className="flextable clickable-rows"
          value={customersProducts}
          paginator={true}
          rows={10}
          loading={isLoading}
          onRowClick={handleRowClick}>
          <Column field="name" header={intl.formatMessage({ id: 'common.name' })} sortable={true}
                  exportable={true}/>
          <Column
            field="address.Org_No"
            header={intl.formatMessage({ id: 'organisation.org-id' })}
            sortable={true}
            exportable={true}
          />
          <Column
            field="all_chemicals_count"
            header={intl.formatMessage({ id: 'admin.max_count' }) + ' ' + intl.formatMessage({ id: 'lang.' + activeMarket })}
            sortable={true}
            exportable={true}
          />
        </DataTable>
      </Section>
      {chartData && (
        <>
          <ToolPanel>
            <DateRangePicker toDate={toDate} fromDate={fromDate} onFromDateChange={setFromDate} onToDateChange={setToDate}/>
          </ToolPanel>
          <Section>
            <Chart type="line" data={chartData} options={chartOptions}/>
          </Section>
        </>
      )}
    </>
  )
}
